import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { toast } from '@/components/ui/toast'
import { clientsApi } from '@/services/clients'
import { getApiErrorMessage } from '@/services/api'
import { isValidEmail } from '@/lib/validations/common'

interface Props {
  onClose: () => void
  clientId: string
  clientEmail: string
  reportCount: number
  search?: string
}

// 2.4.10 Bulk send all client reports via SendGrid.
export function BulkSendClientReportsDialog({
  onClose,
  clientId,
  clientEmail,
  reportCount,
  search,
}: Props) {
  const { t } = useTranslation('clients')
  const [destinationEmail, setDestinationEmail] = useState(clientEmail)
  const [body, setBody] = useState('')
  const [sending, setSending] = useState(false)
  const [result, setResult] = useState<{ sent: number; failed: number } | null>(
    null
  )
  const [destinationEmailError, setDestinationEmailError] = useState('')

  const confirm = async () => {
    if (reportCount === 0) {
      toast({ variant: 'destructive', title: t('reports.bulkSend.noReports') })
      return
    }
    if (!destinationEmail || !isValidEmail(destinationEmail.trim())) {
      setDestinationEmailError(t('reports.bulkSend.invalidRecipient'))
      return
    }
    setSending(true)
    try {
      const res = await clientsApi.bulkSendReports(clientId, {
        recipient_email: destinationEmail.trim(),
        message: body || undefined,
        search: search || undefined,
      })
      const sent = res.data?.sent.length ?? 0
      const failed = res.data?.failed.length ?? 0
      setResult({ sent, failed })
      toast({
        variant: failed > 0 ? 'destructive' : 'success',
        title: t('reports.bulkSend.summary', { sent, failed }),
      })
    } catch (e) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(e, t('reports.bulkSend.error')),
      })
    } finally {
      setSending(false)
    }
  }

  return (
    <Dialog open onOpenChange={o => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('reports.bulkSend.title')}</DialogTitle>
        </DialogHeader>
        {result ? (
          <p className='text-sm'>
            {t('reports.bulkSend.summary', {
              sent: result.sent,
              failed: result.failed,
            })}
          </p>
        ) : (
          <div className='space-y-4 text-sm'>
            <p>
              <Trans
                t={t}
                i18nKey='reports.bulkSend.intro'
                values={{ count: reportCount }}
                components={[<strong key='0' />]}
              />
            </p>
            <div className='space-y-2'>
              <Label htmlFor='bulk-destination-email'>
                {t('reports.bulkSend.to')}
              </Label>
              <Input
                id='bulk-destination-email'
                value={destinationEmail}
                onChange={e => {
                  setDestinationEmail(e.target.value)
                  setDestinationEmailError('')
                }}
                className={
                  destinationEmailError ? 'border-destructive' : undefined
                }
              />
              {destinationEmailError && (
                <p className='text-xs text-destructive'>
                  {destinationEmailError}
                </p>
              )}
            </div>
            <div className='space-y-2'>
              <Label htmlFor='bulk-body'>
                {t('reports.bulkSend.emailBody')}
              </Label>
              <Textarea
                id='bulk-body'
                rows={3}
                maxLength={5000}
                value={body}
                onChange={e => setBody(e.target.value)}
              />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant='outline' onClick={onClose}>
            {result
              ? t('reports.bulkSend.close')
              : t('reports.bulkSend.cancel')}
          </Button>
          {!result && (
            <Button disabled={sending} onClick={confirm}>
              {t('reports.bulkSend.confirm')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
